import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft, Camera, ChevronRight, CircleAlert, ClipboardList,
  FileDown, Image, Mic, Pencil, Plus, Save, Search, Sparkles, Trash2, Users
} from './icons'
import { applyChickPrefixRule, emptyDraft, isDeadObservation, normalizeDraft, normalizeRing, uppercaseText, validateDraft } from './domain'
import {
  AnalysisConfigurationError, analyzeFieldCrops, analyzeSheetPhoto, applyAnalysisProposal, clearDoubt,
  doubtReason, draftFieldValue, draftPhotoFile, getAnalysisConfig, hasManualSheetData, rememberAnalysisCorrection, saveAnalysisConfig,
  type AnalysisConfig
} from './analysis'
import {
  findDuplicateChickRings, getExcelImportInfo, getSheet, listBirdsForStatusReview, listBirdStatusOverrides, listFamilyRecords, listRingPrefixes, listSheetDetails, listSheets, replaceExcelSearchIndex, saveBirdStatusOverride, saveRingPrefix, saveSheet, searchBirds, searchExcelBirds
} from './db/database'
import { readStoredFile } from './storage/fileStore'
import { preparePhoto } from './storage/photo'
import { analyzeSheetQuick, mergeResolvedFields, type HybridProgress, type HybridResult } from './hybrid-analysis'
import { exportPalomarExcel } from './export-excel'
import { parseExcelBirds } from './excel-search'
import { buildBirdFamily, findChildrenForBird } from './family'
import { birdStatusIdentity, classifyBirdStatus, resolveBirdStatus, type BirdStatusKey } from './bird-status'
import { classifyMoreChicks, mergeSpokenProposal, normalizeSpokenRing, normalizeSpokenSearch, normalizeSpokenSex, parseSpokenSheet, type SpokenSheetProposal } from './voice'
import type {
  BirdFamily, BirdInput, BirdSearchResult, BirdStatusOverrides, ChickInput, ExcelImportInfo, FamilyRecord, FieldEvidence, ManualBirdStatus, SheetAnalysis, SheetDetail, SheetDraft, SheetSummary, StoredPhoto
} from './types'

type Screen = 'home' | 'capture' | 'form' | 'sheets' | 'detail' | 'pending' | 'export' | 'search' | 'family'
const plumageHint = 'Ej. Gavino, Toscado, Moracho…'

function usePhotoUrl(file?: File, stored?: StoredPhoto | { reference: string; mime?: string }) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    let active = true
    let objectUrl = ''
    const load = async () => {
      if (file) {
        objectUrl = URL.createObjectURL(file)
      } else if (stored?.reference) {
        const bytes = await readStoredFile(stored.reference)
        if (bytes) {
          const contents = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
          objectUrl = URL.createObjectURL(new Blob([contents], { type: stored.mime || 'image/jpeg' }))
        }
      }
      if (active) setUrl(objectUrl)
      else if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    void load()
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [file, stored?.reference, stored?.mime])
  return url
}

function Header({ title, back }: { title: string; back?: () => void }) {
  return (
    <header className="topbar">
      {back ? <button className="icon-button" onClick={back} aria-label="Volver"><ArrowLeft /></button> : <div className="brand-mark"><img src="/icon-192.png" alt="" /></div>}
      <div><span className="eyebrow">Palomar</span><h1>{title}</h1></div>
      <div className="local-pill"><span /> Local</div>
    </header>
  )
}

function HomeCard({ icon, title, text, onClick, disabled }: {
  icon: React.ReactNode; title: string; text: string; onClick?: () => void; disabled?: boolean
}) {
  return (
    <button className="home-card" onClick={onClick} disabled={disabled}>
      <span className="card-icon">{icon}</span>
      <span className="card-copy"><strong>{title}</strong><small>{text}</small></span>
      {disabled ? <span className="phase-tag">Próximamente</span> : <ChevronRight className="chevron" />}
    </button>
  )
}

function Home({ navigate, count, pendingCount }: { navigate: (screen: Screen) => void; count: number; pendingCount: number }) {
  return (
    <main className="page home-page">
      <Header title="Hojas de cría" />
      <section className="hero">
        <span className="hero-kicker"><Sparkles size={15} /> Registro privado</span>
        <h2>Registro de palomos<br />TEAM</h2>
        <p>Parejas, pichones y genealogía en un solo lugar.</p>
        <button className="primary hero-button" onClick={() => navigate('capture')}><Plus /> Nueva hoja</button>
      </section>
      <section className="quick-grid" aria-label="Acciones">
        <HomeCard icon={<ClipboardList />} title="Hojas" text={count ? `${count} guardada${count === 1 ? '' : 's'}` : 'Todavía no hay hojas'} onClick={() => navigate('sheets')} />
        <HomeCard icon={<CircleAlert />} title="Revisar pendientes" text={pendingCount ? `${pendingCount} pendiente${pendingCount === 1 ? '' : 's'}` : 'Todo revisado'} onClick={() => navigate('pending')} />
        <HomeCard icon={<Search />} title="Buscar palomo" text="Por anilla o nombre" onClick={() => navigate('search')} />
        <HomeCard icon={<FileDown />} title="Exportar" text="Libro Excel por años" onClick={() => navigate('export')} />
      </section>
      <div className="privacy-note">
        <span className="privacy-icon">✓</span>
        <div><strong>Tus datos viven en este dispositivo</strong><p>La base SQLite y las fotografías se guardan localmente.</p></div>
      </div>
    </main>
  )
}

function CapturePhoto({ back, next, manual }: { back: () => void; next: (photos: File[], firstPrepared: File, anio: number, prefijo: string) => void; manual: (anio: number, prefijo: string) => void }) {
  const [photos, setPhotos] = useState<File[]>([])
  const [anio, setAnio] = useState(new Date().getFullYear())
  const [prefijo, setPrefijo] = useState('')
  const [knownPrefixes, setKnownPrefixes] = useState<Record<number, string>>({})
  const [prepared, setPrepared] = useState<File>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const camera = useRef<HTMLInputElement>(null)
  const gallery = useRef<HTMLInputElement>(null)
  const url = usePhotoUrl(prepared)

  useEffect(() => {
    void listRingPrefixes().then(rules => {
      const values = Object.fromEntries(rules.map(rule => [rule.anio, rule.prefijo]))
      setKnownPrefixes(values)
      setPrefijo(values[anio] || '')
    })
  }, [])

  const changeYear = (value: string) => {
    const nextYear = Number(value)
    setAnio(nextYear)
    setPrefijo(knownPrefixes[nextYear] || '')
  }
  const saveSetup = () => saveRingPrefix(anio, prefijo)

  const selected = async (files?: FileList | File[], append = false) => {
    const selectedPhotos = Array.from(files || []).filter(file => file.type.startsWith('image/'))
    if (!selectedPhotos.length) return
    setBusy(true)
    setError('')
    try {
      if (append && photos.length) {
        setPhotos(current => [...current, ...selectedPhotos])
      } else {
        setPhotos(selectedPhotos)
        setPrepared(await preparePhoto(selectedPhotos[0]))
      }
    } catch (cause) {
      if (!append) {
        setPhotos([])
        setPrepared(undefined)
      }
      setError(cause instanceof Error ? cause.message : 'No se pudo abrir la fotografía.')
    } finally {
      setBusy(false)
      if (camera.current) camera.current.value = ''
      if (gallery.current) gallery.current.value = ''
    }
  }

  const clearSelection = () => {
    setPhotos([])
    setPrepared(undefined)
    setError('')
  }

  return (
    <main className="page capture-page">
      <Header title="Nueva hoja" back={back} />
      <input ref={camera} className="hidden-file" type="file" accept="image/*" capture="environment" onChange={event => void selected(event.target.files || undefined, photos.length > 0)} />
      <input ref={gallery} className="hidden-file" type="file" accept="image/*" multiple onChange={event => void selected(event.target.files || undefined, photos.length > 0)} />
      {!prepared ? (
        <section className="capture-choice">
          <div className="capture-copy"><span className="number-badge">01</span><h2>Indica el año y las letras</h2><p>Se usarán desde el primer análisis para interpretar correctamente las anillas de los pichones.</p></div>
          <div className="capture-setup">
            <label className="field"><span>Año de las hojas</span><input type="number" value={anio} onChange={event => changeYear(event.target.value)} /></label>
            <label className="field"><span>Letras iniciales de los pichones</span><input value={prefijo} maxLength={4} placeholder="EJ. AK" onChange={event => setPrefijo(uppercaseText(event.target.value).replace(/[^A-Z]/g, '').slice(0, 4))} onBlur={() => void saveSetup()} /></label>
          </div>
          <div className="capture-copy secondary-step"><span className="number-badge">02</span><h2>Añade las fotografías</h2><p>Puedes hacer una foto o seleccionar varias hojas de la galería. Se revisarán y guardarán de una en una.</p></div>
          <button className="capture-option camera-option" onClick={() => camera.current?.click()} disabled={busy}>
            <span><Camera /></span><div><strong>Hacer foto</strong><small>Abre preferentemente la cámara trasera</small></div><ChevronRight />
          </button>
          <button className="capture-option" onClick={() => gallery.current?.click()} disabled={busy}>
            <span><Image /></span><div><strong>Elegir una o varias</strong><small>Selecciona todas las hojas que quieras procesar</small></div><ChevronRight />
          </button>
          <button className="capture-option manual-option" onClick={() => { void saveSetup().then(() => manual(anio, prefijo)) }} disabled={busy}>
            <span><Pencil /></span><div><strong>Crear hoja manualmente</strong><small>Sin fotografía, OCR ni consumo de API</small></div><ChevronRight />
          </button>
          {busy && <p className="processing-note">Preparando primera fotografía…</p>}
          {error && <div className="error-box"><CircleAlert /><p>{error}</p></div>}
        </section>
      ) : (
        <section className="capture-review">
          <div className="step-heading"><span className="number-badge">03</span><div><h2>Revisa la selección</h2><p>{photos.length === 1 ? 'Has seleccionado una hoja.' : `Has seleccionado ${photos.length} hojas. Se abrirán en orden.`}</p></div></div>
          <div className="capture-setup batch-setup">
            <label className="field"><span>Año de las hojas</span><input type="number" value={anio} onChange={event => changeYear(event.target.value)} /></label>
            <label className="field"><span>Letras iniciales de los pichones</span><input value={prefijo} maxLength={4} placeholder="EJ. AK" onChange={event => setPrefijo(uppercaseText(event.target.value).replace(/[^A-Z]/g, '').slice(0, 4))} onBlur={() => void saveSetup()} /></label>
          </div>
          <div className="batch-count"><ClipboardList /><strong>{photos.length} hoja{photos.length === 1 ? '' : 's'} · {anio} · {prefijo || 'SIN LETRAS'}</strong></div>
          <div className="large-photo"><img src={url} alt="Primera fotografía seleccionada" /></div>
          <p className="photo-quality">Primera hoja · optimizada para lectura</p>
          <div className="review-actions batch-review-actions">
            <button className="secondary" disabled={busy} onClick={() => camera.current?.click()}><Camera /> Añadir otra foto</button>
            <button className="secondary" disabled={busy} onClick={() => gallery.current?.click()}><Image /> Añadir desde galería</button>
            <button className="outline clear-batch" disabled={busy} onClick={clearSelection}>Vaciar selección</button>
            <button className="primary analyze-batch" disabled={!prefijo || busy} onClick={() => { void saveSetup().then(() => next(photos, prepared, anio, prefijo)) }}><Sparkles /> {photos.length === 1 ? 'Analizar con IA' : `Analizar ${photos.length} con IA`}</button>
          </div>
          {!prefijo && <p className="prefix-required">Indica primero las letras de las anillas para analizar.</p>}
        </section>
      )}
    </main>
  )
}
function EvidenceBadge({ evidence }: { evidence?: FieldEvidence }) {
  if (!evidence) return null
  return <span className={`evidence-badge ${evidence.origin.toLocaleLowerCase()} ${evidence.doubtful ? 'low' : ''}`} title={evidence.reason || evidence.variant}>{evidence.origin} · {evidence.confidence}%</span>
}

type SpeechRecognitionLike = {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  onresult: ((event: { results: ArrayLike<{ 0: { transcript: string }; isFinal?: boolean }>; resultIndex?: number }) => void) | null
  onerror: (() => void) | null
  onend: (() => void) | null
  stop?: () => void
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

function VoiceButton({ onResult, label }: { onResult: (value: string) => void; label: string }) {
  const [listening, setListening] = useState(false)
  const speechWindow = window as typeof window & { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor }
  const Constructor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition
  if (!Constructor) return null
  const listen = () => {
    const recognition = new Constructor()
    recognition.lang = 'es-ES'
    recognition.continuous = false
    recognition.interimResults = false
    recognition.maxAlternatives = 1
    recognition.onresult = event => onResult(uppercaseText(event.results[0]?.[0]?.transcript || ''))
    recognition.onerror = () => setListening(false)
    recognition.onend = () => setListening(false)
    setListening(true)
    recognition.start()
  }
  return <button type="button" className={`voice-button ${listening ? 'listening' : ''}`} onClick={listen} aria-label={`Dictar ${label}`} title={`Dictar ${label}`}><Mic /></button>
}

type GuidedVoiceStage = 'father' | 'mother' | 'chick' | 'more'
type GuidedVoiceMessage = { role: 'assistant' | 'user'; text: string }

function GuidedVoiceDictation({ apply }: { apply: (proposal: SpokenSheetProposal) => void }) {
  const [active, setActive] = useState(false)
  const [listening, setListening] = useState(false)
  const [messages, setMessages] = useState<GuidedVoiceMessage[]>([])
  const [ready, setReady] = useState<SpokenSheetProposal>()
  const [error, setError] = useState('')
  const proposalRef = useRef<SpokenSheetProposal>({ padre: {}, madre: {}, pichones: [] })
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const speechWindow = window as typeof window & { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor }
  const Constructor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition
  if (!Constructor) return null
  const Recognition = Constructor

  function addMessage(role: GuidedVoiceMessage['role'], text: string) {
    setMessages(current => [...current, { role, text: uppercaseText(text) }])
  }

  function speak(text: string, after?: () => void) {
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'es-ES'
    utterance.rate = 0.82
    utterance.pitch = 1
    utterance.voice = window.speechSynthesis.getVoices().find(voice => voice.lang.toLowerCase().startsWith('es')) || null
    utterance.onend = () => { if (after) window.setTimeout(after, 900) }
    window.speechSynthesis.speak(utterance)
  }

  function prompt(text: string, stage: GuidedVoiceStage) {
    addMessage('assistant', text)
    speak(text, () => listen(stage))
  }

  function listen(stage: GuidedVoiceStage) {
    const recognition = new Recognition()
    recognition.lang = 'es-ES'
    recognition.continuous = false
    recognition.interimResults = false
    recognition.maxAlternatives = 1
    recognition.onresult = event => {
      const answer = uppercaseText(Array.from(event.results).map(result => result[0]?.transcript || '').join(' '))
      recognition.onend = null
      setListening(false)
      addMessage('user', answer)
      processAnswer(stage, answer)
    }
    recognition.onerror = () => {
      setListening(false)
      setError('No se pudo escuchar. Pulsa Reintentar cuando estés preparado.')
    }
    recognition.onend = () => setListening(false)
    recognitionRef.current = recognition
    setError('')
    setListening(true)
    recognition.start()
  }

  function processAnswer(stage: GuidedVoiceStage, answer: string) {
    if (stage === 'father' || stage === 'mother') {
      const label = stage === 'father' ? 'PADRE' : 'MADRE'
      const parsed = parseSpokenSheet(`${label} ${answer}`)[stage === 'father' ? 'padre' : 'madre']
      if (!Object.keys(parsed).length) { prompt(`No he entendido los datos del ${stage === 'father' ? 'padre' : 'madre'}. Repítelos más despacio.`, stage); return }
      proposalRef.current = { ...proposalRef.current, [stage === 'father' ? 'padre' : 'madre']: parsed }
      if (stage === 'father') prompt('Perfecto. Ahora dime el nombre, plumaje y anilla de la madre.', 'mother')
      else prompt('Ahora dime los datos del primer pichón: anilla, plumaje, sexo y observaciones. Si no indicas cuántos hay, empezaremos por uno.', 'chick')
      return
    }

    if (stage === 'chick') {
      const parsed = parseSpokenSheet(`PICHÓN ${answer}`).pichones[0] || {}
      if (!Object.keys(parsed).length) { prompt('No he entendido los datos del pichón. Repítelos más despacio.', 'chick'); return }
      proposalRef.current = { ...proposalRef.current, pichones: [...proposalRef.current.pichones, parsed] }
      prompt('Pichón anotado. ¿Quieres añadir algún pichón más? Responde sí o no.', 'more')
      return
    }

    const continuation = classifyMoreChicks(answer)
    if (continuation === 'yes') {
      const number = proposalRef.current.pichones.length + 1
      prompt(`Dime los datos del pichón número ${number}: anilla, plumaje, sexo y observaciones.`, 'chick')
    } else if (continuation === 'no') {
      const finished = { ...proposalRef.current, pichones: [...proposalRef.current.pichones] }
      setReady(finished)
      apply(finished)
      setActive(false)
      addMessage('assistant', 'Conversación terminada. He preparado una fila para el padre, otra para la madre y una por cada pichón. Revisa los datos antes de guardar la hoja.')
      speak('Conversación terminada. He pasado los datos al formulario. Revisa las filas antes de guardar.')
    } else prompt('No he entendido si quieres añadir otro. Responde solamente sí o no.', 'more')
  }

  function begin() {
    proposalRef.current = { padre: {}, madre: {}, pichones: [] }
    setMessages([])
    setReady(undefined)
    setError('')
    setActive(true)
    prompt('Empezamos por el padre. Dime su nombre, plumaje y anilla. Habla despacio; esperaré a que termines.', 'father')
  }

  function cancel() {
    recognitionRef.current?.stop?.()
    window.speechSynthesis.cancel()
    setListening(false)
    setActive(false)
    setError('')
  }

  const retry = () => {
    const lastAssistant = [...messages].reverse().find(message => message.role === 'assistant')?.text || ''
    const stage: GuidedVoiceStage = lastAssistant.includes('SÍ O NO') ? 'more' : lastAssistant.includes('MADRE') ? 'mother' : lastAssistant.includes('PICHÓN') || lastAssistant.includes('PICHON') ? 'chick' : 'father'
    listen(stage)
  }

  return (
    <section className={`guided-voice ${active ? 'active' : ''}`}>
      <div className="guided-voice-heading"><span><Mic /></span><div><strong>Conversación guiada</strong><p>La app te preguntará por padre, madre y cada pichón, esperando entre respuestas.</p></div></div>
      {!active && !ready && <button type="button" className="primary guided-start" onClick={begin}><Mic /> Iniciar conversación</button>}
      {messages.length > 0 && <div className="voice-chat" aria-live="polite">{messages.map((message, index) => <p className={message.role} key={`${message.role}-${index}`}><span>{message.role === 'assistant' ? 'PALOMAR' : 'TÚ'}</span>{message.text}</p>)}{listening && <div className="voice-listening"><i className="spinner" /> Escuchando… habla con calma</div>}</div>}
      {error && <div className="voice-error-actions"><p className="voice-error">{error}</p><button type="button" className="secondary" onClick={retry}>Reintentar</button></div>}
      {active && <button type="button" className="guided-cancel" onClick={cancel}>Cancelar conversación</button>}
      {ready && <div className="guided-ready">
        <strong>Datos entendidos y preparados</strong>
        <p>UNA FILA DE PADRE · UNA DE MADRE · {ready.pichones.length} DE PICHÓN</p>
        <div className="guided-result-table" role="table" aria-label="Datos entendidos durante la conversación">
          <div className="guided-result-row header" role="row"><span>TIPO</span><span>NOMBRE</span><span>ANILLA</span><span>PLUMAJE</span><span>SEXO</span><span>OBSERVACIONES</span></div>
          {[
            { role: 'PADRE', bird: ready.padre },
            { role: 'MADRE', bird: ready.madre },
            ...ready.pichones.map((bird, index) => ({ role: `PICHÓN ${index + 1}`, bird })),
          ].map(row => <div className="guided-result-row" role="row" key={row.role}>
            <strong>{row.role}</strong><span>{row.bird.nombre || '—'}</span><span>{row.bird.anilla || '—'}</span><span>{row.bird.plumaje || '—'}</span><span>{row.bird.sexo || '—'}</span><span>{row.bird.observaciones || '—'}</span>
          </div>)}
        </div>
        <button type="button" className="primary" onClick={() => document.getElementById('voice-form-data')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Ver y corregir en el formulario</button>
        <button type="button" className="secondary" onClick={begin}>Repetir conversación</button>
      </div>}
      <small>No guarda automáticamente. Podrás corregir todos los campos antes de guardar.</small>
    </section>
  )
}
function SheetVoiceDictation({ apply }: { apply: (proposal: SpokenSheetProposal) => void }) {
  const [listening, setListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState('')
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const speechWindow = window as typeof window & { SpeechRecognition?: SpeechRecognitionConstructor; webkitSpeechRecognition?: SpeechRecognitionConstructor }
  const Constructor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition
  if (!Constructor) return null

  const start = () => {
    const recognition = new Constructor()
    recognition.lang = 'es-ES'
    recognition.continuous = true
    recognition.interimResults = false
    recognition.maxAlternatives = 1
    recognition.onresult = event => {
      const heard = Array.from(event.results).map(result => result[0]?.transcript || '').join(' ')
      setTranscript(uppercaseText(heard))
    }
    recognition.onerror = () => { setListening(false); setError('No se pudo escuchar. Revisa el permiso del micrófono.') }
    recognition.onend = () => setListening(false)
    recognitionRef.current = recognition
    setTranscript('')
    setError('')
    setListening(true)
    recognition.start()
  }

  const stop = () => {
    recognitionRef.current?.stop?.()
    setListening(false)
  }

  const useTranscript = () => {
    const proposal = parseSpokenSheet(transcript)
    const recognized = Object.keys(proposal.padre).length + Object.keys(proposal.madre).length + proposal.pichones.reduce((total, chick) => total + Object.keys(chick).length, 0)
    if (!recognized) { setError('No se reconocieron bloques como PADRE, MADRE o PICHÓN. Puedes corregir el texto y volver a aplicar.'); return }
    apply(proposal)
    setError('')
  }

  return (
    <section className={`sheet-voice ${listening ? 'listening' : ''}`}>
      <div className="sheet-voice-title"><span><Mic /></span><div><strong>Dictar toda la hoja seguida</strong><p>Di seguido: «Padre…, plumaje…, anilla…; madre…; primer pichón, anilla…, plumaje…, sexo…».</p></div></div>
      {!listening ? <button type="button" className="primary voice-start" onClick={start}><Mic /> Empezar dictado</button> : <button type="button" className="voice-stop" onClick={stop}><span /> Terminar dictado</button>}
      {(transcript || listening) && <label className="voice-transcript"><span>Texto entendido — puedes corregirlo</span><textarea value={transcript} placeholder="Habla ahora…" onChange={event => setTranscript(uppercaseText(event.target.value))} /></label>}
      {transcript && !listening && <button type="button" className="secondary voice-apply" onClick={useTranscript}>Rellenar formulario con este dictado</button>}
      {error && <p className="voice-error">{error}</p>}
      <small>No guarda nada: primero rellena el formulario para que puedas revisarlo.</small>
    </section>
  )
}
function Field({ label, value, onChange, placeholder, type = 'text', doubtful, evidence, voice = false }: {
  label: string; value: string | number; onChange: (value: string) => void; placeholder?: string; type?: string; doubtful?: string; evidence?: FieldEvidence; voice?: boolean
}) {
  return <label className={`field ${doubtful ? 'doubtful' : ''}`}><span>{label}<EvidenceBadge evidence={evidence} />{doubtful && <em className="review-tag">Revisar</em>}</span><div className="field-control"><input type={type} value={value} placeholder={placeholder} onChange={event => onChange(type === 'number' ? event.target.value : uppercaseText(event.target.value))} />{voice && <VoiceButton label={label} onResult={value => onChange(uppercaseText(label).includes('ANILLA') ? normalizeSpokenRing(value) : value)} />}</div>{doubtful && <small className="doubt-reason">{doubtful}</small>}</label>
}
function BirdFields({ title, bird, prefix, doubt, evidence, onChange }: {
  title: string; bird: BirdInput; prefix: 'padre' | 'madre'; doubt: (path: string) => string | undefined; evidence: (path: string) => FieldEvidence | undefined; onChange: (bird: BirdInput, path: string) => void
}) {
  return (
    <section className="form-section">
      <div className="section-title"><span className="sex-dot">{title === 'Padre' ? '♂' : '♀'}</span><h3>{title}</h3></div>
      <div className="field-grid">
        <Field voice label="Nombre" value={bird.nombre} placeholder="Nombre del palomo" doubtful={doubt(`${prefix}.nombre`)} evidence={evidence(`${prefix}.nombre`)} onChange={nombre => onChange({ ...bird, nombre }, `${prefix}.nombre`)} />
        <Field voice label="Nº de anilla" value={bird.anilla} placeholder="AK-500153" doubtful={doubt(`${prefix}.anilla`)} evidence={evidence(`${prefix}.anilla`)} onChange={anilla => onChange({ ...bird, anilla }, `${prefix}.anilla`)} />
        <Field voice label="Plumaje" value={bird.plumaje} placeholder={plumageHint} doubtful={doubt(`${prefix}.plumaje`)} evidence={evidence(`${prefix}.plumaje`)} onChange={plumaje => onChange({ ...bird, plumaje }, `${prefix}.plumaje`)} />
      </div>
    </section>
  )
}

function ChickCard({ chick, index, doubt, evidence, onChange, onRemove }: {
  chick: ChickInput; index: number; doubt: (path: string) => string | undefined; evidence: (path: string) => FieldEvidence | undefined; onChange: (chick: ChickInput, path: string) => void; onRemove: () => void
}) {
  const prefix = `pichones.${index}`
  return (
    <article className="chick-card">
      <div className="chick-head"><strong>Pichón {index + 1}</strong><button className="delete-button" onClick={onRemove}><Trash2 /> Eliminar</button></div>
      <div className="field-grid">
        <Field voice label="Nº de anilla" value={chick.anilla} placeholder="AK-500153" doubtful={doubt(`${prefix}.anilla`)} evidence={evidence(`${prefix}.anilla`)} onChange={anilla => onChange({ ...chick, anilla }, `${prefix}.anilla`)} />
        <Field voice label="Plumaje" value={chick.plumaje} placeholder={plumageHint} doubtful={doubt(`${prefix}.plumaje`)} evidence={evidence(`${prefix}.plumaje`)} onChange={plumaje => onChange({ ...chick, plumaje }, `${prefix}.plumaje`)} />
        <label className={`field ${doubt(`${prefix}.sexo`) ? 'doubtful' : ''}`}><span>Sexo<EvidenceBadge evidence={evidence(`${prefix}.sexo`)} />{doubt(`${prefix}.sexo`) && <em className="review-tag">Revisar</em>}</span><div className="field-control"><select value={chick.sexo} onChange={event => onChange({ ...chick, sexo: event.target.value }, `${prefix}.sexo`)}><option value="">SIN INDICAR</option><option>MACHO</option><option>HEMBRA</option><option>DUDOSO</option></select><VoiceButton label="sexo" onResult={value => onChange({ ...chick, sexo: normalizeSpokenSex(value) }, `${prefix}.sexo`)} /></div>{doubt(`${prefix}.sexo`) && <small className="doubt-reason">{doubt(`${prefix}.sexo`)}</small>}</label>
        <label className={`field full ${doubt(`${prefix}.observaciones`) ? 'doubtful' : ''}`}><span>Observaciones<EvidenceBadge evidence={evidence(`${prefix}.observaciones`)} />{doubt(`${prefix}.observaciones`) && <em className="review-tag">Revisar</em>}</span><div className="field-control textarea-control"><textarea value={chick.observaciones} placeholder="Notas, estado, fila tachada…" onChange={event => onChange({ ...chick, observaciones: uppercaseText(event.target.value) }, `${prefix}.observaciones`)} /><VoiceButton label="observaciones" onResult={value => onChange({ ...chick, observaciones: uppercaseText([chick.observaciones, value].filter(Boolean).join('; ')) }, `${prefix}.observaciones`)} /></div>{doubt(`${prefix}.observaciones`) && <small className="doubt-reason">{doubt(`${prefix}.observaciones`)}</small>}</label>
      </div>
    </article>
  )
}

function AnalysisSettings({ initial, save, cancel }: { initial: AnalysisConfig; save: (config: AnalysisConfig) => void; cancel: () => void }) {
  const [url, setUrl] = useState(initial.url)
  const [token, setToken] = useState(initial.token)
  return (
    <section className="analysis-settings">
      <div><strong>Configurar análisis</strong><p>Estos datos conectan la app con tu Worker. La clave de OpenAI nunca se introduce aquí.</p></div>
      <label><span>URL del Worker</span><input type="url" value={url} placeholder="https://palomar-vision-api….workers.dev/api/analyze-sheet" onChange={event => setUrl(event.target.value)} /></label>
      <label><span>Código de análisis</span><input type="password" value={token} autoComplete="off" onChange={event => setToken(event.target.value)} /></label>
      <div className="settings-actions"><button className="secondary" onClick={cancel}>Cancelar</button><button className="primary" disabled={!url.trim() || !token.trim()} onClick={() => save({ url: url.trim(), token: token.trim() })}>Guardar configuración</button></div>
    </section>
  )
}

function SheetForm({ initial, autoOcr, autoAnalyze, batchLabel, back, saved }: {
  initial: SheetDraft; autoOcr?: boolean; autoAnalyze?: boolean; batchLabel?: string; back: () => void; saved: (result: { id: string; warnings: string[] }) => void
}) {
  const [draft, setDraft] = useState<SheetDraft>(() => normalizeDraft(initial))
  const [errors, setErrors] = useState<string[]>([])
  const [duplicates, setDuplicates] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [manualDirty, setManualDirty] = useState(hasManualSheetData(initial))
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [analysisError, setAnalysisError] = useState('')
  const [pendingAnalysis, setPendingAnalysis] = useState<SheetAnalysis>()
  const [analysisConfig, setAnalysisConfig] = useState(getAnalysisConfig)
  const [showSettings, setShowSettings] = useState(false)
  const [quickStatus, setQuickStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [quickProgress, setQuickProgress] = useState<HybridProgress>({ stage: '', current: 0, total: 1 })
  const [quickError, setQuickError] = useState('')
  const [hybridResult, setHybridResult] = useState<HybridResult>()
  const [pendingHybrid, setPendingHybrid] = useState<HybridResult>()
  const autoOcrStarted = useRef(false)
  const autoAnalysisStarted = useRef(false)
  const correctionSources = useRef<Record<string, FieldEvidence>>({})
  const photoUrl = usePhotoUrl(draft.photo, draft.storedPhoto)
  const [ringPrefixes, setRingPrefixes] = useState<Record<number, string>>({})
  const yearPrefix = ringPrefixes[draft.anio] || ''

  useEffect(() => {
    void listRingPrefixes().then(rules => setRingPrefixes(Object.fromEntries(rules.map(rule => [rule.anio, rule.prefijo]))))
  }, [])

  const manualChange = (next: SheetDraft, path: string) => {
    const source = correctionSources.current[path] || draft.fieldEvidence?.[path]
    if (source?.origin === 'IA') rememberAnalysisCorrection(path, source.value, draftFieldValue(next, path))
    const cleared = clearDoubt(next, path)
    const fieldEvidence = { ...cleared.fieldEvidence }
    delete fieldEvidence[path]
    setDraft(applyChickPrefixRule({ ...cleared, fieldEvidence }, ringPrefixes[next.anio] || ''))
    setManualDirty(true)
    setPendingAnalysis(undefined)
    setDuplicates([])
  }

  const changeYearPrefix = (value: string) => {
    const clean = uppercaseText(value).replace(/[^A-Z]/g, '').slice(0, 4)
    setRingPrefixes(current => ({ ...current, [draft.anio]: clean }))
    setDraft(current => applyChickPrefixRule(current, clean))
  }

  const persistYearPrefix = () => { void saveRingPrefix(draft.anio, yearPrefix) }

  const addChick = () => {
    setManualDirty(true)
    setDraft(current => ({
      ...current,
      pichones: [...current.pichones, { id: crypto.randomUUID(), anilla: '', nombre: '', plumaje: '', sexo: '', observaciones: '' }],
      doubtfulFields: current.doubtfulFields?.filter(item => !item.campo.startsWith('pichones.'))
    }))
  }

  const applyFullVoice = (proposal: SpokenSheetProposal) => {
    setDraft(current => applyChickPrefixRule(mergeSpokenProposal(current, proposal), yearPrefix))
    setManualDirty(true)
    setPendingAnalysis(undefined)
    setPendingHybrid(undefined)
    setDuplicates([])
    setErrors([])
  }
  const replacePhoto = async (file?: File) => {
    if (!file) return
    setBusy(true)
    try {
      const prepared = await preparePhoto(file)
      setDraft(current => ({ ...current, photo: prepared, doubtfulFields: [] }))
      setQuickStatus('idle')
      setQuickError('')
      setHybridResult(undefined)
      setAnalysisStatus('idle')
      setAnalysisError('')
    } finally {
      setBusy(false)
    }
  }

  const acceptAnalysis = (proposal: SheetAnalysis, prefixes = ringPrefixes) => {
    setDraft(current => {
      const proposalDraft = applyAnalysisProposal(current, proposal)
      const next = applyChickPrefixRule(proposalDraft, prefixes[proposalDraft.anio] || '')
      correctionSources.current = { ...next.fieldEvidence }
      return next
    })
    setPendingAnalysis(undefined)
    setAnalysisStatus('success')
    setAnalysisError('')
    setManualDirty(false)
  }

  const acceptHybrid = (result: HybridResult) => {
    setDraft(current => {
      const proposalDraft = { ...applyAnalysisProposal(current, result.proposal), fieldEvidence: result.evidence }
      const next = applyChickPrefixRule(proposalDraft, ringPrefixes[proposalDraft.anio] || '')
      correctionSources.current = { ...next.fieldEvidence }
      return next
    })
    setHybridResult(result)
    setPendingHybrid(undefined)
    setQuickStatus('success')
    setManualDirty(false)
  }

  const runOcr = async () => {
    if (quickStatus === 'loading') return
    setQuickStatus('loading')
    setQuickError('')
    setQuickProgress({ stage: 'Preparando imagen', current: 0, total: 1 })
    try {
      const photo = await draftPhotoFile(draft)
      if (!photo) throw new Error('Esta hoja no tiene una fotografía disponible para leer.')
      const result = await analyzeSheetQuick(photo, setQuickProgress)
      if (manualDirty) {
        setPendingHybrid(result)
        setQuickStatus('success')
      } else acceptHybrid(result)
    } catch (error) {
      setQuickStatus('error')
      setQuickError(error instanceof Error ? error.message : 'No se pudo completar el análisis rápido.')
    }
  }

  const resolveDoubts = async () => {
    if (!hybridResult) return
    const currentConfig = getAnalysisConfig()
    if (!currentConfig.url || !currentConfig.token) {
      setAnalysisConfig(currentConfig); setShowSettings(true); setAnalysisStatus('error'); setAnalysisError('Configura primero el Worker y el código de análisis.'); return
    }
    const items = Object.entries(draft.fieldEvidence || {}).filter(([, evidence]) => evidence.doubtful).flatMap(([campo, evidence]) => hybridResult.crops[campo] ? [{ campo, ocr: evidence.value, crop: hybridResult.crops[campo] }] : [])
    if (!items.length) return
    setAnalysisStatus('loading'); setAnalysisError('')
    try {
      const resolved = await analyzeFieldCrops(items)
      setDraft(current => {
        const next = mergeResolvedFields(current, resolved)
        correctionSources.current = { ...correctionSources.current, ...next.fieldEvidence }
        return next
      })
      setAnalysisStatus('success')
    } catch (error) {
      if (error instanceof AnalysisConfigurationError) setShowSettings(true)
      setAnalysisStatus('error'); setAnalysisError(error instanceof Error ? error.message : 'No se pudieron resolver las dudas.')
    }
  }

  const runAnalysis = async () => {
    if (analysisStatus === 'loading') return
    const currentConfig = getAnalysisConfig()
    if (!currentConfig.url || !currentConfig.token) {
      setAnalysisConfig(currentConfig)
      setShowSettings(true)
      setAnalysisStatus('error')
      setAnalysisError('Configura primero el Worker y el código de análisis.')
      return
    }
    setAnalysisStatus('loading')
    setAnalysisError('')
    setPendingAnalysis(undefined)
    try {
      const photo = await draftPhotoFile(draft)
      if (!photo) throw new Error('Esta hoja no tiene una fotografía disponible para analizar.')
      const rules = await listRingPrefixes()
      const prefixMap: Record<number, string> = { ...Object.fromEntries(rules.map(rule => [rule.anio, rule.prefijo])), ...ringPrefixes }
      setRingPrefixes(prefixMap)
      const selectedPrefix = prefixMap[draft.anio] || ''
      const guidance = selectedPrefix ? { [draft.anio]: selectedPrefix } : prefixMap
      const analyzed = await analyzeSheetPhoto(photo, guidance)
      const proposal = { ...analyzed, anio: draft.anio }
      if (manualDirty) {
        setPendingAnalysis(proposal)
        setAnalysisStatus('success')
      } else {
        acceptAnalysis(proposal, prefixMap)
      }
    } catch (error) {
      if (error instanceof AnalysisConfigurationError) setShowSettings(true)
      setAnalysisStatus('error')
      setAnalysisError(error instanceof Error ? error.message : 'Error al analizar la fotografía.')
    }
  }

  useEffect(() => {
    if (autoOcr && !autoOcrStarted.current) {
      autoOcrStarted.current = true
      void runOcr()
    }
  }, [autoOcr])

  useEffect(() => {
    if (autoAnalyze && !autoAnalysisStarted.current) {
      autoAnalysisStarted.current = true
      void runAnalysis()
    }
  }, [autoAnalyze])

  const configureAnalysis = (config: AnalysisConfig) => {
    saveAnalysisConfig(config)
    setAnalysisConfig(config)
    setShowSettings(false)
    setAnalysisError('')
    setAnalysisStatus('idle')
    void runAnalysis()
  }

  const submit = async (acceptDuplicates = false) => {
    const checked = applyChickPrefixRule(draft, yearPrefix)
    setDraft(checked)
    const normalized = normalizeDraft(checked)
    const nextErrors = validateDraft(normalized)
    if (nextErrors.length) {
      setErrors(nextErrors)
      return
    }
    setBusy(true)
    setErrors([])
    try {
      const found = await findDuplicateChickRings(normalized)
      if (found.length && !acceptDuplicates) {
        setDuplicates(found)
        return
      }
      const result = await saveSheet(normalized)
      saved(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setErrors([message.includes('UNIQUE constraint failed: hojas_cria') ? 'Ya existe una hoja con ese año y número.' : 'No se pudo guardar. Comprueba los datos e inténtalo de nuevo.'])
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="page form-page">
      <Header title={draft.id ? 'Editar hoja' : batchLabel ? 'Nueva hoja ' + batchLabel : 'Nueva hoja'} back={back} />
      <div className="form-intro"><span>Datos de la hoja</span><p>Todos los campos de padre, madre y pichones son opcionales.</p></div>
      <GuidedVoiceDictation apply={applyFullVoice} />
      <SheetVoiceDictation apply={applyFullVoice} />
      <section className="form-photo-summary">
        {photoUrl ? <img src={photoUrl} alt="Fotografía de la hoja" /> : <div className="no-photo"><Image /><span>Sin fotografía</span></div>}
        <div>
          <strong>Fotografía asociada</strong>
          <label className="small-photo-button"><Camera /> Cambiar foto<input type="file" accept="image/*" capture="environment" onChange={event => void replacePhoto(event.target.files?.[0])} /></label>
        </div>
      </section>
      {(draft.photo || draft.storedPhoto) ? <section className="hybrid-panel">
        <div className="analysis-modes">
          <button className={`mode-button quick ${quickStatus}`} disabled={quickStatus === 'loading' || analysisStatus === 'loading' || busy} onClick={() => void runOcr()}>
            <Search /><span><strong>Analizar rápido</strong><small>Preprocesado + OCR por celdas</small></span>{quickStatus === 'loading' && <i className="spinner" />}
          </button>
          <button className={`mode-button ai ${analysisStatus}`} disabled={quickStatus === 'loading' || analysisStatus === 'loading' || busy} onClick={() => void runAnalysis()}>
            <Sparkles /><span><strong>Analizar con IA</strong><small>Hoja completa como respaldo</small></span>{analysisStatus === 'loading' && <i className="spinner" />}
          </button>
        </div>
        {quickStatus === 'loading' && <div className="ocr-progress"><span style={{ width: `${Math.round(quickProgress.current / Math.max(1, quickProgress.total) * 100)}%` }} /><small>{quickProgress.stage}{quickProgress.field ? ` · ${quickProgress.field}` : ''}</small></div>}
        {quickStatus === 'success' && hybridResult && <div className="hybrid-summary"><span>✓</span><div><strong>OCR por zonas completado</strong><p>{Object.keys(draft.fieldEvidence || {}).length} campos detectados · {draft.doubtfulFields?.length || 0} para revisar · deskew {hybridResult.preprocessing.skewDegrees.toFixed(1)}°</p></div></div>}
        {quickError && <div className="error-box ocr-error"><CircleAlert /><p>{quickError}</p></div>}
        {hybridResult && (draft.doubtfulFields?.length || 0) > 0 && <button className="resolve-ai" disabled={analysisStatus === 'loading'} onClick={() => void resolveDoubts()}><Sparkles /> {analysisStatus === 'loading' ? 'Resolviendo recortes…' : `Resolver ${draft.doubtfulFields?.length} dudas con IA`}</button>}
        <button className="configure-analysis subtle" onClick={() => { setAnalysisConfig(getAnalysisConfig()); setShowSettings(value => !value) }}>Configurar API</button>
      </section> : <div className="manual-sheet-note"><Pencil /><div><strong>Alta manual</strong><p>Rellena los datos y pulsa Guardar. No se usará OCR ni la API.</p></div></div>}
      {showSettings && <AnalysisSettings initial={analysisConfig} save={configureAnalysis} cancel={() => setShowSettings(false)} />}
      {analysisStatus === 'success' && !pendingAnalysis && <div className="analysis-success"><span>✓</span><div><strong>Análisis completado</strong><p>La propuesta se ha rellenado. Revisa especialmente los campos marcados antes de guardar.</p></div></div>}
      {analysisStatus === 'error' && analysisError && <div className="error-box analysis-error"><CircleAlert /><p>{analysisError}</p></div>}
      {pendingAnalysis && <div className="overwrite-warning"><CircleAlert /><div><strong>El formulario contiene datos</strong><p>El análisis está listo. Si lo aplicas, sustituirá los datos actuales de padre, madre y pichones. La fotografía no cambiará y nada se guardará todavía.</p><div><button className="secondary" onClick={() => { setPendingAnalysis(undefined); setAnalysisStatus('idle') }}>Cancelar</button><button className="primary" onClick={() => acceptAnalysis(pendingAnalysis)}>Sustituir por la propuesta</button></div></div></div>}
      {pendingHybrid && <div className="overwrite-warning"><CircleAlert /><div><strong>El formulario contiene datos</strong><p>El OCR por zonas está listo. Aplicarlo sustituirá los campos actuales, pero todavía no guardará la hoja.</p><div><button className="secondary" onClick={() => { setPendingHybrid(undefined); setQuickStatus('idle') }}>Cancelar</button><button className="primary" onClick={() => acceptHybrid(pendingHybrid)}>Aplicar propuesta OCR</button></div></div></div>}
      {draft.doubtfulFields && draft.doubtfulFields.length > 0 && <div className="doubt-summary"><CircleAlert /><span>{draft.doubtfulFields.length} campo{draft.doubtfulFields.length === 1 ? '' : 's'} para revisar</span></div>}

      <section className="form-section sheet-id" id="voice-form-data">
        <div className="section-title"><span className="number-badge">01</span><h3>Identificación</h3></div>
        <div className="field-grid two">
          <Field label="Año" type="number" value={draft.anio} doubtful={doubtReason(draft, 'anio')} evidence={draft.fieldEvidence?.anio} onChange={anio => manualChange({ ...draft, anio: Number(anio) }, 'anio')} />
          <Field label="Nº de hoja" type="number" value={draft.hoja} doubtful={doubtReason(draft, 'hoja')} evidence={draft.fieldEvidence?.hoja} onChange={hoja => manualChange({ ...draft, hoja: Number(hoja) }, 'hoja')} />
        </div>
        <label className="field ring-prefix-field">
          <span>LETRAS INICIALES DE LOS PICHONES EN {draft.anio}</span>
          <input value={yearPrefix} maxLength={4} placeholder="EJ. AK" onChange={event => changeYearPrefix(event.target.value)} onBlur={persistYearPrefix} />
          <small>Se guarda una vez por año. Ayuda a interpretar las anillas, pero nunca cambia una lectura distinta sin marcarla para revisar.</small>
        </label>
      </section>
      <BirdFields title="Padre" prefix="padre" bird={draft.padre} doubt={path => doubtReason(draft, path)} evidence={path => draft.fieldEvidence?.[path]} onChange={(padre, path) => manualChange({ ...draft, padre }, path)} />
      <BirdFields title="Madre" prefix="madre" bird={draft.madre} doubt={path => doubtReason(draft, path)} evidence={path => draft.fieldEvidence?.[path]} onChange={(madre, path) => manualChange({ ...draft, madre }, path)} />

      <section className="form-section chicks-section">
        <div className="section-title"><span className="number-badge">04</span><div><h3>Pichones</h3><small>{draft.pichones.length} añadidos</small></div></div>
        {draft.pichones.map((chick, index) => (
          <ChickCard key={chick.id} chick={chick} index={index} doubt={path => doubtReason(draft, path)} evidence={path => draft.fieldEvidence?.[path]}
            onChange={(next, path) => manualChange({ ...draft, pichones: draft.pichones.map(item => item.id === next.id ? next : item) }, path)}
            onRemove={() => { setManualDirty(true); setDuplicates([]); setDraft({ ...draft, pichones: draft.pichones.filter(item => item.id !== chick.id), doubtfulFields: draft.doubtfulFields?.filter(item => !item.campo.startsWith('pichones.')) }) }} />
        ))}
        <button className="outline add-chick" onClick={addChick}><Plus /> Añadir pichón</button>
      </section>

      {duplicates.length > 0 && <div className="warning-box"><CircleAlert /><div><strong>Anilla de pichón repetida</strong><p>{duplicates.join(', ')} ya existe en otra hoja o está repetida en esta. Revisa los datos antes de continuar.</p><button onClick={() => void submit(true)}>Guardar de todos modos</button></div></div>}
      {errors.length > 0 && <div className="error-box"><CircleAlert /><div>{errors.map(error => <p key={error}>{error}</p>)}</div></div>}
      <div className="sticky-actions"><button className="primary" disabled={busy || analysisStatus === 'loading' || quickStatus === 'loading'} onClick={() => void submit()}><Save /> {busy ? 'Guardando…' : 'Guardar hoja'}</button></div>
    </main>
  )
}

function Thumbnail({ sheet }: { sheet: SheetSummary }) {
  const url = usePhotoUrl(undefined, sheet.photoReference ? { reference: sheet.photoReference, mime: sheet.photoMime } : undefined)
  return <div className="sheet-thumb">{url ? <img src={url} alt="" /> : <ClipboardList />}</div>
}

function Sheets({ back, sheets, open }: { back: () => void; sheets: SheetSummary[]; open: (id: string) => void }) {
  return (
    <main className="page">
      <Header title="Hojas" back={back} />
      <div className="list-heading"><div><span className="eyebrow">Archivo local</span><h2>{sheets.length} hojas guardadas</h2></div></div>
      <section className="sheet-list">
        {sheets.length === 0 ? <div className="empty-state"><ClipboardList /><h3>Aún no hay hojas</h3><p>La primera aparecerá aquí cuando la guardes.</p></div> :
          sheets.map(sheet => <button className="sheet-row" key={sheet.id} onClick={() => open(sheet.id)}><Thumbnail sheet={sheet} /><div className="sheet-number"><strong>{sheet.hoja}</strong><small>{sheet.anio}</small></div><div className="sheet-copy"><h3>{sheet.padre || 'Sin padre'} × {sheet.madre || 'Sin madre'}</h3><p>{sheet.pichones} {sheet.pichones === 1 ? 'pichón' : 'pichones'}</p></div><ChevronRight /></button>)}
      </section>
    </main>
  )
}

function parentSummaryText(parent?: { nombre: string; anilla: string; plumaje: string }) {
  return [parent?.nombre, parent?.anilla, parent?.plumaje].filter(Boolean).join(' · ') || 'SIN DATOS'
}

function SearchStatistics({ query, results }: { query: string; results: BirdSearchResult[] }) {
  const totals = results.reduce((value, bird) => {
    const sex = uppercaseText(bird.sexo).trim()
    const dead = isDeadObservation(bird.observaciones) || bird.appearances.some(item => item.deceased || isDeadObservation(item.observaciones || ''))
    if (sex === 'M' || sex === 'MACHO') value.males += 1
    else if (sex === 'H' || sex === 'HEMBRA') value.females += 1
    else value.unknown += 1
    if (dead) {
      value.dead += 1
      if (sex === 'M' || sex === 'MACHO') value.deadMales += 1
      else if (sex === 'H' || sex === 'HEMBRA') value.deadFemales += 1
    }
    return value
  }, { males: 0, females: 0, unknown: 0, dead: 0, deadMales: 0, deadFemales: 0 })
  const pairs = new Map<string, { label: string; count: number }>()
  const seenPairs = new Set<string>()
  results.forEach(bird => bird.appearances.filter(item => item.role === 'PICHÓN').forEach(item => {
    const father = parentSummaryText(item.padre)
    const mother = parentSummaryText(item.madre)
    if (father === 'SIN DATOS' && mother === 'SIN DATOS') return
    const key = `${normalizeRing(item.padre?.anilla || '') || uppercaseText(item.padre?.nombre || '')}|${normalizeRing(item.madre?.anilla || '') || uppercaseText(item.madre?.nombre || '')}`
    const uniqueKey = `${bird.source}-${bird.id}-${key}`
    if (seenPairs.has(uniqueKey)) return
    seenPairs.add(uniqueKey)
    const current = pairs.get(key) || { label: `${father} × ${mother}`, count: 0 }
    current.count += 1
    pairs.set(key, current)
  }))
  return (
    <section className="search-statistics">
      <h2>«{query}»: {results.length} palomo{results.length === 1 ? '' : 's'} en total</h2>
      <div className="statistics-grid">
        <div><strong>{totals.males}</strong><span>Machos</span></div>
        <div><strong>{totals.females}</strong><span>Hembras</span></div>
        <div><strong>{totals.unknown}</strong><span>Sin sexo</span></div>
        <div className="dead"><strong>{totals.dead}</strong><span>Muertos</span><small>{totals.deadMales} machos · {totals.deadFemales} hembras</small></div>
      </div>
      {pairs.size > 0 && <div className="statistics-pairs"><strong>Parejas de origen</strong>{[...pairs.values()].sort((a, b) => b.count - a.count).map(pair => <p key={pair.label}><span>{pair.label}</span><b>{pair.count}</b></p>)}</div>}
    </section>
  )
}

function BirdChildren({ bird, records, openSheet, openFamily }: { bird: BirdSearchResult; records: FamilyRecord[]; openSheet: (id: string) => void; openFamily: (ring: string) => void }) {
  const children = findChildrenForBird(bird, records)
  if (!children.length) return null
  const parentRing = normalizeRing(bird.anilla)
  const parentName = uppercaseText(bird.nombre).trim()
  return (
    <details className="search-section search-children">
      <summary><span><Users /> HIJOS</span><b>{children.length}</b><ChevronRight /></summary>
      <div className="search-section-content">
        {children.map((child, index) => {
          const asFather = parentRing ? normalizeRing(child.padre.anilla) === parentRing : uppercaseText(child.padre.nombre).trim() === parentName
          return (
            <article key={`${child.source}-${child.birdId}-${child.sheetId}-${index}`}>
              <button disabled={!child.anilla} onClick={() => child.anilla && openFamily(child.anilla)}>
                <div><span>{child.anilla || 'SIN ANILLA'}</span><strong>{child.nombre || 'SIN NOMBRE'}</strong><small>{asFather ? 'HIJO POR PARTE DE PADRE' : 'HIJO POR PARTE DE MADRE'} · HOJA {child.hoja}/{child.anio}</small></div>
                {child.deceased && <b>MUERTO</b>}{child.anilla && <ChevronRight />}
              </button>
              {child.sheetId && <button className="child-sheet-link" onClick={() => openSheet(child.sheetId)}>ABRIR HOJA</button>}
            </article>
          )
        })}
      </div>
    </details>
  )
}
function birdDescriptionSignature(bird: BirdSearchResult) {
  return [...new Set([bird.observaciones, ...bird.appearances.map(item => item.observaciones || '')]
    .filter(Boolean).map(value => uppercaseText(value).trim()))].sort().join('|')
}

const searchStatusFilters: { key: 'all' | BirdStatusKey; label: string }[] = [
  { key: 'all', label: 'TODOS' },
  { key: 'home', label: 'CASA' },
  { key: 'gifted', label: 'REGALADO' },
  { key: 'lost', label: 'PERDIDO' },
  { key: 'dead', label: 'MUERTO' },
  { key: 'pending', label: 'PENDIENTE' },
]

const searchRoleFilters: { key: 'all' | 'PADRE' | 'MADRE' | 'PICHÓN'; label: string }[] = [
  { key: 'all', label: 'TODOS' },
  { key: 'PADRE', label: 'PADRE' },
  { key: 'MADRE', label: 'MADRE' },
  { key: 'PICHÓN', label: 'PICHÓN' },
]

const statusChoices: { key: ManualBirdStatus; label: string }[] = [
  { key: 'home', label: 'CASA' },
  { key: 'gifted', label: 'REGALADO' },
  { key: 'lost', label: 'PERDIDO' },
  { key: 'dead', label: 'MUERTO' },
]

function PendingStatusReview({ back, changed }: { back: () => void; changed: () => void }) {
  const [birds, setBirds] = useState<BirdSearchResult[]>([])
  const [overrides, setOverrides] = useState<BirdStatusOverrides>({})
  const [selections, setSelections] = useState<BirdStatusOverrides>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [applySame, setApplySame] = useState<Record<string, boolean>>({})
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([listBirdsForStatusReview(), listBirdStatusOverrides()])
      .then(([items, saved]) => { setBirds(items); setOverrides(saved) })
      .catch(() => setError('NO SE PUDIERON CARGAR LOS PALOMOS PENDIENTES.'))
      .finally(() => setLoading(false))
  }, [])

  const reviewItems = useMemo(() => birds.filter(bird => {
    const key = birdStatusIdentity(bird)
    return classifyBirdStatus(bird).key === 'pending' && !overrides[key]
  }), [birds, overrides])
  const selectedCount = reviewItems.filter(bird => Boolean(selections[birdStatusIdentity(bird)])).length

  const selectStatus = (bird: BirdSearchResult, status: ManualBirdStatus) => {
    const key = birdStatusIdentity(bird)
    const signature = birdDescriptionSignature(bird)
    const targets = applySame[key] && signature
      ? reviewItems.filter(item => birdDescriptionSignature(item) === signature)
      : [bird]
    setSelections(current => ({
      ...current,
      ...Object.fromEntries(targets.map(item => [birdStatusIdentity(item), status]))
    }))
  }

  const saveSelections = async () => {
    const targets = reviewItems.filter(bird => Boolean(selections[birdStatusIdentity(bird)]))
    if (!targets.length) return
    setSaving(true)
    setError('')
    try {
      await Promise.all(targets.map(bird => {
        const key = birdStatusIdentity(bird)
        return saveBirdStatusOverride(key, selections[key])
      }))
      const saved = Object.fromEntries(targets.map(bird => {
        const key = birdStatusIdentity(bird)
        return [key, selections[key]]
      })) as BirdStatusOverrides
      setOverrides(current => ({ ...current, ...saved }))
      setSelections(current => Object.fromEntries(Object.entries(current).filter(([key]) => !saved[key])))
      changed()
    } catch {
      setError('NO SE PUDIERON GUARDAR LAS SELECCIONES. INTÉNTALO DE NUEVO.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="page pending-page">
      <Header title="Pendientes" back={back} />
      <section className="pending-intro">
        <CircleAlert />
        <div><strong>{reviewItems.length} PENDIENTE{reviewItems.length === 1 ? '' : 'S'} DE DECIDIR</strong><p>MARCA LOS ESTADOS Y PULSA GUARDAR. DESPUÉS DESAPARECERÁN DE ESTA LISTA.</p></div>
      </section>
      {error && <div className="error-box"><CircleAlert /><p>{error}</p></div>}
      {loading && <div className="empty-state standalone"><i className="spinner" /><h3>Cargando pendientes…</h3></div>}
      {!loading && reviewItems.length === 0 && <div className="empty-state standalone"><CircleAlert /><h3>Todo revisado</h3><p>NO HAY DESCRIPCIONES DUDOSAS EN ESTE DISPOSITIVO.</p></div>}
      <section className="pending-list">
        {reviewItems.map(bird => {
          const key = birdStatusIdentity(bird)
          const selected = selections[key]
          const observations = [...new Set([bird.observaciones, ...bird.appearances.map(item => item.observaciones || '')].filter(Boolean))]
          const signature = birdDescriptionSignature(bird)
          const matchingCount = signature ? reviewItems.filter(item => birdDescriptionSignature(item) === signature).length : 1
          return <article className={`pending-bird ${selected ? 'reviewed' : ''}`} key={key}>
            <div className="pending-bird-head">
              <div><span>{bird.anilla || 'SIN ANILLA'}</span><h2>{bird.nombre || 'SIN NOMBRE'}</h2></div>
              <span className={`source-badge ${bird.source === 'EXCEL' ? 'excel' : 'app'}`}>{bird.source === 'EXCEL' ? 'EXCEL' : 'APP'}</span>
            </div>
            <div className="pending-bird-data">
              <DetailValue label="Plumaje" value={bird.plumaje} />
              <DetailValue label="Sexo" value={bird.sexo} />
              <DetailValue label="Descripción" value={observations.join(' · ') || 'SIN DESCRIPCIÓN'} />
              <DetailValue label="Hojas" value={bird.appearances.map(item => `${item.hoja}/${item.anio}`).join(', ')} />
            </div>
            {matchingCount > 1 && <label className="same-description-option">
              <input type="checkbox" checked={Boolean(applySame[key])} onChange={event => setApplySame(current => ({ ...current, [key]: event.target.checked }))} />
              <span>APLICAR TAMBIÉN A LOS {matchingCount} PALOMOS CON ESTA MISMA DESCRIPCIÓN</span>
            </label>}
            <fieldset className="status-options" disabled={saving}>
              <legend>SELECCIONA EL ESTADO</legend>
              {statusChoices.map(choice => <label className={`${choice.key} ${selected === choice.key ? 'selected' : ''}`} key={choice.key}>
                <input type="radio" name={`status-${key}`} checked={selected === choice.key} onChange={() => selectStatus(bird, choice.key)} />
                <span>{choice.label}</span>
              </label>)}
            </fieldset>
            {selected && <p className="review-saved">✓ SELECCIÓN PREPARADA. PULSA GUARDAR PARA CONFIRMARLA.</p>}
          </article>
        })}
      </section>
      {reviewItems.length > 0 && <div className="pending-save-bar">
        <button className="primary" disabled={!selectedCount || saving} onClick={() => void saveSelections()}><Save /> {saving ? 'GUARDANDO…' : `GUARDAR ${selectedCount || ''} SELECCIÓN${selectedCount === 1 ? '' : 'ES'}`}</button>
      </div>}
    </main>
  )
}
function BirdStatusBadge({ bird, manual }: { bird: BirdSearchResult; manual?: ManualBirdStatus }) {
  const status = resolveBirdStatus(bird, manual)
  return <span className={`bird-status ${status.key}`} title={status.reason}>{status.label}</span>
}
function BirdStatusEditor({ bird, manual, saved }: { bird: BirdSearchResult; manual?: ManualBirdStatus; saved: (status: ManualBirdStatus) => void }) {
  const automatic = classifyBirdStatus(bird)
  const current = resolveBirdStatus(bird, manual)
  const [choice, setChoice] = useState<ManualBirdStatus | ''>(current.key === 'pending' ? '' : current.key)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    const status = resolveBirdStatus(bird, manual)
    setChoice(status.key === 'pending' ? '' : status.key)
  }, [bird, manual])

  const save = async () => {
    if (!choice) return
    setSaving(true)
    setDone(false)
    try {
      await saveBirdStatusOverride(birdStatusIdentity(bird), choice)
      saved(choice)
      setDone(true)
    } finally {
      setSaving(false)
    }
  }

  if (automatic.key === 'dead') return <p className="dead-status-lock">MUERTO PROCEDE DE LA DESCRIPCIÓN Y TIENE PRIORIDAD. PARA CAMBIARLO, CORRIGE PRIMERO LA DESCRIPCIÓN DE LA HOJA.</p>

  return <details className="bird-status-editor">
    <summary><Pencil /> EDITAR ESTADO</summary>
    <fieldset className="status-options compact" disabled={saving}>
      <legend>SELECCIONA EL NUEVO ESTADO</legend>
      {statusChoices.map(option => <label className={`${option.key} ${choice === option.key ? 'selected' : ''}`} key={option.key}>
        <input type="radio" name={`edit-status-${bird.source}-${bird.id}`} checked={choice === option.key} onChange={() => { setChoice(option.key); setDone(false) }} />
        <span>{option.label}</span>
      </label>)}
    </fieldset>
    <button className="primary status-editor-save" disabled={!choice || saving} onClick={() => void save()}><Save /> {saving ? 'GUARDANDO…' : 'GUARDAR ESTADO'}</button>
    {done && <p className="review-saved">✓ ESTADO ACTUALIZADO.</p>}
  </details>
}
function BirdSearch({ back, openSheet, openFamily }: { back: () => void; openSheet: (id: string) => void; openFamily: (ring: string) => void }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<BirdSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [excelInfo, setExcelInfo] = useState<ExcelImportInfo | null>(null)
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState('')
  const [familyRecords, setFamilyRecords] = useState<FamilyRecord[]>([])
  const [statusOverrides, setStatusOverrides] = useState<BirdStatusOverrides>({})
  const [statusFilter, setStatusFilter] = useState<'all' | BirdStatusKey>('all')
  const [roleFilter, setRoleFilter] = useState<'all' | 'PADRE' | 'MADRE' | 'PICHÓN'>('all')
  const displayedResults = useMemo(() => results.filter(bird => {
    const matchesStatus = statusFilter === 'all' || resolveBirdStatus(bird, statusOverrides[birdStatusIdentity(bird)]).key === statusFilter
    const matchesRole = roleFilter === 'all' || bird.appearances.some(appearance => appearance.role === roleFilter)
    return matchesStatus && matchesRole
  }), [results, statusFilter, roleFilter, statusOverrides])
  const statusCounts = useMemo(() => {
    const counts: Record<'all' | BirdStatusKey, number> = { all: results.length, home: 0, gifted: 0, lost: 0, dead: 0, pending: 0 }
    results.forEach(bird => {
      const status = resolveBirdStatus(bird, statusOverrides[birdStatusIdentity(bird)]).key
      counts[status] += 1
    })
    return counts
  }, [results, statusOverrides])
  const roleCounts = useMemo(() => ({
    all: results.length,
    PADRE: results.filter(bird => bird.appearances.some(appearance => appearance.role === 'PADRE')).length,
    MADRE: results.filter(bird => bird.appearances.some(appearance => appearance.role === 'MADRE')).length,
    PICHÓN: results.filter(bird => bird.appearances.some(appearance => appearance.role === 'PICHÓN')).length,
  }), [results])

  const ringStats = useMemo(() => {
    const stats = new Map<string, { total: number; dead: number }>()
    displayedResults.forEach(bird => {
      const ring = normalizeRing(bird.anilla)
      if (!ring) return
      const current = stats.get(ring) || { total: 0, dead: 0 }
      bird.appearances.forEach(appearance => {
        current.total += 1
        if (appearance.deceased || isDeadObservation(appearance.observaciones || bird.observaciones)) current.dead += 1
      })
      stats.set(ring, current)
    })
    return stats
  }, [displayedResults])

  useEffect(() => {
    void getExcelImportInfo().then(setExcelInfo)
    void listBirdStatusOverrides().then(setStatusOverrides)
  }, [])

  const runSearch = async (value: string) => {
    const [local, excel, families] = await Promise.all([searchBirds(value), searchExcelBirds(value), listFamilyRecords()])
    setResults([...local, ...excel])
    setFamilyRecords(families)
    setSearched(true)
  }

  useEffect(() => {
    const value = query.trim()
    if (!value) {
      setResults([])
      setSearched(false)
      setLoading(false)
      return
    }
    setLoading(true)
    const timer = window.setTimeout(() => {
      void runSearch(value)
        .catch(() => { setResults([]); setSearched(true) })
        .finally(() => setLoading(false))
    }, 180)
    return () => window.clearTimeout(timer)
  }, [query])

  const importExcel = async (file?: File) => {
    if (!file) return
    setImporting(true)
    setImportError('')
    try {
      const birds = await parseExcelBirds(await file.arrayBuffer())
      if (!birds.length) throw new Error('No se encontraron palomos en las pestañas anuales del Excel.')
      const info = await replaceExcelSearchIndex(file.name, birds)
      setExcelInfo(info)
      if (query.trim()) await runSearch(query.trim())
    } catch (cause) {
      setImportError(cause instanceof Error ? cause.message : 'No se pudo leer el Excel.')
    } finally {
      setImporting(false)
    }
  }

  return (
    <main className="page search-page">
      <Header title="Buscar palomo" back={back} />
      <section className={`excel-search-source ${excelInfo ? 'ready' : ''}`}>
        <div className="excel-source-copy">
          <FileDown />
          <div>
            <strong>{excelInfo ? 'Excel disponible para buscar' : 'Añadir datos del Excel'}</strong>
            <p>{excelInfo ? `${excelInfo.birds} palomos · ${excelInfo.filename}` : 'Selecciona el Excel completo una vez. Se indexará localmente.'}</p>
          </div>
        </div>
        <label className="excel-source-button">
          {importing ? <><i className="spinner" /> Leyendo…</> : excelInfo ? 'Actualizar Excel' : 'Seleccionar Excel'}
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={importing} onChange={event => void importExcel(event.target.files?.[0])} />
        </label>
      </section>
      {importError && <div className="error-box"><CircleAlert /><p>{importError}</p></div>}

      <section className="bird-search-box">
        <Search />
        <input
          autoFocus
          value={query}
          inputMode="search"
          placeholder="NOMBRE, ANILLA O DESCRIPCIÓN"
          aria-label="Buscar por nombre o anilla"
          onChange={event => setQuery(uppercaseText(event.target.value))}
        />
        <VoiceButton label="búsqueda de palomos" onResult={value => setQuery(normalizeSpokenSearch(value))} />
        {loading && <i className="spinner" />}
      </section>
      <p className="search-help">Busca por nombre, anilla, plumaje, sexo u observaciones, tanto en la app como en el Excel.</p>
      <span className="filter-heading">ESTADO</span>
      <div className="status-filter" role="group" aria-label="Filtrar por estado">
        {searchStatusFilters.map(filter => <button className={statusFilter === filter.key ? 'selected' : ''} onClick={() => setStatusFilter(filter.key)} key={filter.key}><span>{filter.label}</span><b>{statusCounts[filter.key]}</b></button>)}
      </div>
      <span className="filter-heading">FUNCIÓN EN LA HOJA</span>
      <div className="status-filter role-filter" role="group" aria-label="Filtrar por padre, madre o pichón">
        {searchRoleFilters.map(filter => <button className={roleFilter === filter.key ? 'selected' : ''} onClick={() => setRoleFilter(filter.key)} key={filter.key}><span>{filter.label}</span><b>{roleCounts[filter.key]}</b></button>)}
      </div>

      {!query.trim() && <div className="empty-state search-empty"><Search /><h3>Busca cualquier palomo</h3><p>Verás si procede de la app, del Excel y todas sus hojas.</p></div>}
      {searched && !loading && results.length === 0 && <div className="empty-state search-empty"><CircleAlert /><h3>No se encontraron resultados</h3><p>Prueba con otra parte del nombre o de la anilla.</p></div>}
      {searched && !loading && results.length > 0 && displayedResults.length === 0 && <div className="empty-state search-empty"><CircleAlert /><h3>No hay resultados con ese estado</h3><p>Selecciona otros filtros para ver los demás palomos.</p></div>}
      {displayedResults.length > 0 && (
        <>
          <div className="search-count">{displayedResults.length} resultado{displayedResults.length === 1 ? '' : 's'}</div>
          <SearchStatistics query={query.trim()} results={displayedResults} />
          <section className="bird-results">
            {displayedResults.map(bird => (
              <article className="bird-result" key={`${bird.source}-${bird.id}`}>
                <div className="bird-result-head">
                  <div><span>{bird.anilla || 'SIN ANILLA'}</span><div className="bird-title-line"><h2>{bird.nombre || 'SIN NOMBRE'}</h2><BirdStatusBadge bird={bird} manual={statusOverrides[birdStatusIdentity(bird)]} /></div></div>
                  <div className="bird-head-badges">
                    <span className={`source-badge ${bird.source === 'EXCEL' ? 'excel' : 'app'}`}>{bird.source === 'EXCEL' ? 'EXCEL' : 'APP'}</span>
                    <span className="bird-role-count">{bird.appearances.length} hoja{bird.appearances.length === 1 ? '' : 's'}</span>
                  </div>
                </div>
                {bird.anilla && (ringStats.get(normalizeRing(bird.anilla))?.total || 0) > 1 && (
                  <div className={`repeated-ring-warning ${(ringStats.get(normalizeRing(bird.anilla))?.dead || 0) > 0 ? 'has-dead' : ''}`}>
                    <CircleAlert />
                    <span>Anilla en {ringStats.get(normalizeRing(bird.anilla))?.total} sitios{(ringStats.get(normalizeRing(bird.anilla))?.dead || 0) > 0 ? ` · ${ringStats.get(normalizeRing(bird.anilla))?.dead} marcado como muerto` : ''}</span>
                  </div>
                )}
                <div className="bird-facts">
                  <DetailValue label="Plumaje" value={bird.plumaje} />
                  <DetailValue label="Sexo" value={bird.sexo} />
                  {(bird.observaciones || bird.appearances.some(item => item.observaciones)) && <DetailValue label="Observaciones" value={bird.observaciones || bird.appearances.find(item => item.observaciones)?.observaciones} />}
                </div>
                <div className="bird-primary-action">
                  {bird.anilla && <button className="open-family-button" onClick={() => openFamily(bird.anilla)}><Users /> Padres, hermanos y árbol <ChevronRight /></button>}
                </div>
                <BirdChildren bird={bird} records={familyRecords} openSheet={openSheet} openFamily={openFamily} />
                <details className="search-section appearances-section">
                  <summary><span><ClipboardList /> APARICIONES EN HOJAS</span><b>{bird.appearances.length}</b><ChevronRight /></summary>
                  <div className="bird-appearances">
                    {bird.appearances.map((appearance, index) => {
                      const deceased = appearance.deceased || isDeadObservation(appearance.observaciones || bird.observaciones)
                      return (
                        <div className={`appearance-entry ${deceased ? 'deceased' : ''}`} key={`${appearance.sheetId || 'excel'}-${appearance.anio}-${appearance.hoja}-${appearance.role}-${index}`}>
                          <button disabled={!appearance.sheetId} onClick={() => appearance.sheetId && openSheet(appearance.sheetId)}>
                            <span>{appearance.role}</span>
                            <strong>Hoja {appearance.hoja}/{appearance.anio}</strong>
                            <div className="appearance-tail">
                              {deceased && <b>MUERTO</b>}
                              {appearance.sheetId ? <ChevronRight /> : <small>Excel</small>}
                            </div>
                          </button>
                          {appearance.role === 'PICHÓN' && (
                            <div className="appearance-parents">
                              <p><span>PADRE</span><strong>{parentSummaryText(appearance.padre)}</strong></p>
                              <p><span>MADRE</span><strong>{parentSummaryText(appearance.madre)}</strong></p>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </details>
                <BirdStatusEditor bird={bird} manual={statusOverrides[birdStatusIdentity(bird)]} saved={status => setStatusOverrides(current => ({ ...current, [birdStatusIdentity(bird)]: status }))} />              </article>
            ))}
          </section>
        </>
      )}
    </main>
  )
}

function FamilyMemberButton({ member, navigate, openSheet }: { member: FamilyRecord; navigate: (ring: string) => void; openSheet: (id: string) => void }) {
  return (
    <article className={`family-member ${member.deceased ? 'deceased' : ''}`}>
      <button className="family-member-main" onClick={() => navigate(member.anilla)}>
        <div><span>{member.anilla}</span><strong>{member.nombre || 'SIN NOMBRE'}</strong><small>{[member.plumaje, member.sexo].filter(Boolean).join(' · ') || 'SIN MÁS DATOS'}</small></div>
        <div>{member.deceased && <b>MUERTO</b>}<ChevronRight /></div>
      </button>
      <div className="family-member-meta"><span>{member.source}</span><span>HOJA {member.hoja}/{member.anio}</span>{member.sheetId && <button onClick={() => openSheet(member.sheetId)}>ABRIR HOJA</button>}</div>
    </article>
  )
}

function FamilySection({ title, description, members, navigate, openSheet }: { title: string; description: string; members: FamilyRecord[]; navigate: (ring: string) => void; openSheet: (id: string) => void }) {
  return (
    <section className="family-section">
      <div className="family-section-title"><div><h2>{title}</h2><p>{description}</p></div><span>{members.length}</span></div>
      {members.length ? members.map((member, index) => <FamilyMemberButton key={`${member.source}-${member.birdId}-${member.sheetId}-${index}`} member={member} navigate={navigate} openSheet={openSheet} />) : <p className="family-empty">No se han encontrado palomos verificables en este grupo.</p>}
    </section>
  )
}

function CloseFamilyTree({ family, parentPairs, records, statusBirds, statusOverrides, navigate, openSheet }: { family: BirdFamily; parentPairs: FamilyRecord[]; records: FamilyRecord[]; statusBirds: BirdSearchResult[]; statusOverrides: BirdStatusOverrides; navigate: (ring: string) => void; openSheet: (id: string) => void }) {
  const target = family.targets[0]

  const statusBirdFor = (record?: FamilyRecord) => {
    if (!record) return undefined
    return statusBirds.find(bird => bird.source === record.source && bird.id === record.birdId)
      || statusBirds.find(bird => bird.source === record.source && normalizeRing(bird.anilla) === normalizeRing(record.anilla))
  }

  const statusBadgeFor = (record?: FamilyRecord) => {
    const bird = statusBirdFor(record)
    return bird ? <BirdStatusBadge bird={bird} manual={statusOverrides[birdStatusIdentity(bird)]} /> : <span className="bird-status unknown">SIN ESTADO</span>
  }

  const uniqueByRing = (members: { record: FamilyRecord; relation?: string }[]) => {
    const seen = new Set<string>()
    return members.filter(({ record }) => {
      const key = normalizeRing(record.anilla) || `${record.source}:${record.birdId}:${record.sheetId}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  const relatedRecord = (ring: string) => records
    .filter(record => normalizeRing(record.anilla) === normalizeRing(ring))
    .sort((left, right) => Number(Boolean(right.nombre)) + Number(Boolean(right.sexo)) - Number(Boolean(left.nombre)) - Number(Boolean(left.sexo)))[0]

  const unclesFor = (ring: string) => {
    if (!ring) return []
    const parentFamily = buildBirdFamily(ring, records)
    return uniqueByRing([
      ...parentFamily.directSiblings.map(record => ({ record, relation: 'HERMANO/A DIRECTO/A' })),
      ...parentFamily.paternalSiblings.map(record => ({ record, relation: 'HERMANO/A DE PADRE' })),
      ...parentFamily.maternalSiblings.map(record => ({ record, relation: 'HERMANO/A DE MADRE' })),
    ])
  }

  const memberCard = (member: FamilyRecord, relation?: string) => <article className={`tree-bird-card ${member.deceased ? 'deceased' : ''}`} key={`${relation || 'familiar'}-${member.source}-${member.birdId}-${member.sheetId}`}>
    <button className="tree-bird-main" disabled={!member.anilla} onClick={() => member.anilla && navigate(member.anilla)}>
      {relation && <small className="tree-relation-label">{relation}</small>}
      <span>{member.anilla || 'SIN ANILLA'}</span>
      <strong>{member.nombre || 'SIN NOMBRE'}</strong>
      <em>{[member.plumaje, member.sexo].filter(Boolean).join(' · ') || 'SIN DATOS ADICIONALES'}</em>
      {member.observaciones && <small className="tree-observation">DESCRIPCIÓN: {member.observaciones}</small>}
      <small>{member.hoja ? `PAREJA / HOJA ${member.hoja} · ${member.anio}` : member.source}</small>
      {statusBadgeFor(member)}
    </button>
    {member.sheetId && <button className="tree-sheet-link" onClick={() => openSheet(member.sheetId)}>ABRIR HOJA</button>}
  </article>

  const siblingSection = (kind: string, title: string, description: string, members: FamilyRecord[]) => <section className={`tree-sibling-group ${kind}`}>
    <div className="tree-group-title"><div><h3>{title}</h3><p>{description}</p></div><b>{members.length}</b></div>
    <div className="tree-members">{members.length ? members.map(member => memberCard(member)) : <p>NINGUNO ENCONTRADO</p>}</div>
  </section>

  const parentBranch = (role: 'PADRE' | 'MADRE', summary: FamilyRecord['padre']) => {
    const record = relatedRecord(summary.anilla)
    const uncles = unclesFor(summary.anilla)
    const paternal = role === 'PADRE'
    return <section className={`tree-parent-branch ${role.toLowerCase()}`}>
      <button className="tree-parent-card" disabled={!summary.anilla} onClick={() => summary.anilla && navigate(summary.anilla)}>
        <small>{role}</small>
        <span>{summary.anilla || 'SIN ANILLA'}</span>
        <strong>{summary.nombre || record?.nombre || 'SIN NOMBRE'}</strong>
        <em>{[summary.plumaje || record?.plumaje, record?.sexo].filter(Boolean).join(' · ') || 'SIN DATOS ADICIONALES'}</em>
        {record?.observaciones && <small className="tree-observation">DESCRIPCIÓN: {record.observaciones}</small>}
        {record?.hoja ? <small>PAREJA / HOJA {record.hoja} · {record.anio}</small> : null}
        {statusBadgeFor(record)}
      </button>

      <div className="tree-uncles-heading"><span>{paternal ? 'Tíos paternos' : 'Tíos maternos'}</span><b>{uncles.length}</b></div>
      <div className="tree-uncles">
        {uncles.length ? uncles.map(({ record: uncle, relation }) => memberCard(uncle, relation)) : <p>No constan hermanos de este progenitor.</p>}
      </div>
    </section>
  }

  return <section className="close-family-tree">
    <div className="close-tree-heading"><div><span>Árbol familiar</span><h2>Familia cercana</h2></div><small>Pulsa cualquier palomo para navegar</small></div>

    <div className="tree-sibling-map tree-sibling-map-top">
      {siblingSection('father', 'Hermanos de padre', 'Mismo padre, distinta madre', family.paternalSiblings)}
      {siblingSection('direct', 'Hermanos directos', 'Mismo padre y misma madre', family.directSiblings)}
      {siblingSection('mother', 'Hermanos de madre', 'Misma madre, distinto padre', family.maternalSiblings)}
    </div>

    <article className="tree-current">
      <small>PALOMO CONSULTADO</small>
      <span>{family.anilla}</span>
      <strong>{target?.nombre || 'SIN NOMBRE'}</strong>
      <em>{[target?.plumaje, target?.sexo].filter(Boolean).join(' · ') || 'SIN DATOS ADICIONALES'}</em>
      {target?.observaciones && <small className="tree-observation">DESCRIPCIÓN: {target.observaciones}</small>}
      {target?.hoja ? <small>PAREJA / HOJA {target.hoja} · {target.anio}</small> : null}
      {statusBadgeFor(target)}
    </article>

    <div className="tree-connector vertical" />
    <div className="tree-parent-pairs">
      {parentPairs.length ? parentPairs.map((record, index) => <article className="tree-parent-pair" key={`${record.source}-${record.sheetId}-${index}`}>
        <div className="tree-pair-origin"><span>PAREJA / HOJA {record.hoja} · {record.anio} · {record.source}</span>{record.sheetId && <button onClick={() => openSheet(record.sheetId)}>ABRIR HOJA</button>}</div>
        <div className="tree-parent-branches">
          {parentBranch('PADRE', record.padre)}
          {parentBranch('MADRE', record.madre)}
        </div>
      </article>) : <p className="family-empty">No constan padre y madre con anillas verificables.</p>}
    </div>


    <p className="pedigree-note">Las relaciones se calculan por las anillas. Por eso siguen vinculadas aunque el nombre esté escrito de forma diferente.</p>
  </section>
}
function FamilyView({ ring, back, navigate, openSheet }: { ring: string; back: () => void; navigate: (ring: string) => void; openSheet: (id: string) => void }) {
  const [familyData, setFamilyData] = useState<{ records: FamilyRecord[]; statusBirds: BirdSearchResult[]; statusOverrides: BirdStatusOverrides }>()
  useEffect(() => {
    setFamilyData(undefined)
    void Promise.all([listFamilyRecords(), listBirdsForStatusReview(), listBirdStatusOverrides()])
      .then(([records, statusBirds, statusOverrides]) => setFamilyData({ records, statusBirds, statusOverrides }))
  }, [ring])
  if (!familyData) return <main className="page"><Header title="Familia" back={back} /><p className="loading">Calculando parentescos y estados…</p></main>
  const { records, statusBirds, statusOverrides } = familyData
  const family = buildBirdFamily(ring, records)
  const parentPairs = family.targets.filter((record, index, items) => items.findIndex(item => normalizeRing(item.padre.anilla) === normalizeRing(record.padre.anilla) && normalizeRing(item.madre.anilla) === normalizeRing(record.madre.anilla)) === index)
  return (
    <main className="page family-page">
      <Header title="Familia del palomo" back={back} />
      <section className="family-hero"><Users /><div><span>Anilla consultada</span><h2>{family.anilla}</h2><p>Hermanos, padres, tíos y estado de cada palomo.</p></div></section>
      <CloseFamilyTree family={family} parentPairs={parentPairs} records={records} statusBirds={statusBirds} statusOverrides={statusOverrides} navigate={navigate} openSheet={openSheet} />
    </main>
  )
}
function DetailValue({ label, value }: { label: string; value?: string }) {
  return <div className="detail-value"><span>{label}</span><strong>{value || '—'}</strong></div>
}

function SheetDetailView({ id, back, edit }: { id: string; back: () => void; edit: (sheet: SheetDetail) => void }) {
  const [sheet, setSheet] = useState<SheetDetail | null>()
  useEffect(() => { void getSheet(id).then(setSheet) }, [id])
  const photoUrl = usePhotoUrl(undefined, sheet?.storedPhoto)

  if (sheet === undefined) return <main className="page"><Header title="Hoja" back={back} /><p className="loading">Cargando hoja…</p></main>
  if (sheet === null) return <main className="page"><Header title="Hoja" back={back} /><div className="empty-state standalone"><CircleAlert /><h3>No se encontró la hoja</h3></div></main>
  return (
    <main className="page detail-page">
      <Header title={`Hoja ${sheet.hoja} · ${sheet.anio}`} back={back} />
      {photoUrl ? <div className="detail-photo"><img src={photoUrl} alt="Fotografía de la hoja de cría" /></div> : <div className="detail-photo empty"><Image /><span>Sin fotografía</span></div>}
      <section className="detail-section"><h2>Pareja</h2><div className="parents-grid"><article><h3>Padre</h3><DetailValue label="Nombre" value={sheet.padre.nombre} /><DetailValue label="Anilla" value={sheet.padre.anilla} /><DetailValue label="Plumaje" value={sheet.padre.plumaje} /></article><article><h3>Madre</h3><DetailValue label="Nombre" value={sheet.madre.nombre} /><DetailValue label="Anilla" value={sheet.madre.anilla} /><DetailValue label="Plumaje" value={sheet.madre.plumaje} /></article></div></section>
      <section className="detail-section"><h2>Pichones <span>{sheet.pichones.length}</span></h2>{sheet.pichones.length ? sheet.pichones.map((chick, index) => <article className="detail-chick" key={chick.id}><strong>Pichón {index + 1}</strong><div className="detail-grid"><DetailValue label="Anilla" value={chick.anilla} /><DetailValue label="Plumaje" value={chick.plumaje} /><DetailValue label="Sexo" value={chick.sexo} /><DetailValue label="Observaciones" value={chick.observaciones} /></div></article>) : <p className="muted-copy">Esta hoja todavía no tiene pichones.</p>}</section>
      <div className="sticky-actions"><button className="primary" onClick={() => edit(sheet)}><Pencil /> Editar hoja</button></div>
    </main>
  )
}

function ExportExcel({ back, sheets, openSheet }: { back: () => void; sheets: SheetSummary[]; openSheet: (id: string) => void }) {
  const [baseFile, setBaseFile] = useState<File>()
  const [reviewId, setReviewId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')
  const years = [...new Set(sheets.map(sheet => sheet.anio))].sort((a, b) => a - b)

  const download = async (file?: File) => {
    setBusy(true)
    setError('')
    setDone('')
    try {
      const details = await listSheetDetails()
      const result = await exportPalomarExcel(details, file)
      const duplicateNote = result.skipped
        ? ` ${result.skipped} hoja${result.skipped === 1 ? '' : 's'} ya estaba${result.skipped === 1 ? '' : 'n'} en el Excel y no se duplicó${result.skipped === 1 ? '' : 'n'}: ${result.skippedSheets.join(', ')}.`
        : ''
      setDone(`${result.filename} descargado correctamente. ${result.added} hoja${result.added === 1 ? '' : 's'} añadida${result.added === 1 ? '' : 's'}.${duplicateNote}`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No se pudo crear el archivo Excel.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="page export-page">
      <Header title="Exportar a Excel" back={back} />
      <section className="export-summary">
        <span className="card-icon"><FileDown /></span>
        <div><span className="eyebrow">Datos de la app</span><h2>{sheets.length} hojas listas</h2><p>{years.length ? `Años: ${years.join(', ')}` : 'Todavía no hay hojas guardadas.'}</p></div>
      </section>

      <section className="export-option export-review">
        <h3>Revisar y corregir antes de exportar</h3>
        <p>Abre cualquier hoja, modifica sus datos y pulsa Guardar. El Excel se generará después con los valores corregidos.</p>
        <select value={reviewId} onChange={event => setReviewId(event.target.value)}>
          <option value="">SELECCIONA UNA HOJA</option>
          {[...sheets].sort((a, b) => a.anio - b.anio || a.hoja - b.hoja).map(sheet => (
            <option key={sheet.id} value={sheet.id}>HOJA {sheet.hoja}/{sheet.anio} · {sheet.padre || 'SIN PADRE'} × {sheet.madre || 'SIN MADRE'}</option>
          ))}
        </select>
        <button className="secondary export-button" disabled={!reviewId} onClick={() => reviewId && openSheet(reviewId)}><Pencil /> Abrir y editar hoja</button>
      </section>

      <section className="export-option recommended">
        <span className="export-step">Recomendado</span>
        <h3>Usar tu Excel de 2023 y 2024</h3>
        <p>Selecciona <strong>Registro_Palomos_REVISION_LIMPIA_v17.xlsx</strong>. La app conservará todas las filas existentes y añadirá las hojas guardadas al final de la pestaña de cada año.</p>
        <label className="file-picker">
          <FileDown />
          <span>{baseFile ? baseFile.name : 'Seleccionar Excel existente'}</span>
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={event => { setBaseFile(event.target.files?.[0]); setDone(''); setError('') }} />
        </label>
        {baseFile && <button className="primary export-button" disabled={busy} onClick={() => void download(baseFile)}><FileDown /> {busy ? 'Preparando Excel…' : 'Añadir al final y descargar'}</button>}
      </section>

      <section className="export-option">
        <h3>Crear un Excel nuevo</h3>
        <p>Descarga únicamente las hojas guardadas en esta app. Cada año aparecerá en una pestaña distinta.</p>
        <button className="secondary export-button" disabled={busy || sheets.length === 0} onClick={() => void download()}><FileDown /> {busy ? 'Preparando Excel…' : 'Descargar Excel nuevo'}</button>
      </section>

      <div className="privacy-note compact"><span className="privacy-icon">✓</span><div><strong>Todo se procesa en tu dispositivo</strong><p>El Excel seleccionado y tus datos no se envían a ningún servidor.</p></div></div>
      {error && <div className="error-box"><CircleAlert /><p>{error}</p></div>}
      {done && <div className="analysis-success"><span>✓</span><div><strong>Exportación terminada</strong><p>{done}</p></div></div>}
    </main>
  )
}

export default function App() {
  const [screen, setScreen] = useState<Screen>('home')
  const [sheets, setSheets] = useState<SheetSummary[]>([])
  const [working, setWorking] = useState<SheetDraft>()
  const [autoOcr, setAutoOcr] = useState(false)
  const [autoAnalyze, setAutoAnalyze] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [detailBack, setDetailBack] = useState<Screen>('sheets')
  const [afterEdit, setAfterEdit] = useState<Screen>('sheets')
  const [toast, setToast] = useState('')
  const [batchPhotos, setBatchPhotos] = useState<File[]>([])
  const [batchTotal, setBatchTotal] = useState(0)
  const [familyRing, setFamilyRing] = useState('')
  const [familyHistory, setFamilyHistory] = useState<string[]>([])
  const [pendingCount, setPendingCount] = useState(0)

  const refresh = () => listSheets().then(setSheets).catch(() => setSheets([]))
  const refreshPendingCount = () => Promise.all([listBirdsForStatusReview(), listBirdStatusOverrides()])
    .then(([birds, overrides]) => setPendingCount(birds.filter(bird => classifyBirdStatus(bird).key === 'pending' && !overrides[birdStatusIdentity(bird)]).length))
    .catch(() => setPendingCount(0))
  useEffect(() => { void refresh(); void refreshPendingCount() }, [])
  useEffect(() => { if (screen === 'sheets') void refresh(); if (screen === 'home') void refreshPendingCount() }, [screen])
  useEffect(() => {
    const scrollToField = (event: FocusEvent) => {
      const field = event.target
      if (!(field instanceof HTMLElement) || !field.matches('input:not([type="file"]):not([type="hidden"]), textarea, select')) return
      window.setTimeout(() => field.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120)
    }
    document.addEventListener('focusin', scrollToField)
    return () => document.removeEventListener('focusin', scrollToField)
  }, [])

  const showToast = (message: string) => {
    setToast(message)
    window.setTimeout(() => setToast(''), 5000)
  }

  const onSaved = async (result: { id: string; warnings: string[] }) => {
    const wasEditing = Boolean(working?.id)
    void refresh()
    setAutoOcr(false)
    if (batchPhotos.length) {
      const [nextPhoto, ...remaining] = batchPhotos
      try {
        const prepared = await preparePhoto(nextPhoto)
        setBatchPhotos(remaining)
        setWorking({ ...emptyDraft(), anio: working?.anio || new Date().getFullYear(), photo: prepared })
        setAutoAnalyze(true)
        setScreen('form')
        window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0)
        showToast(result.warnings.length ? `Hoja guardada con aviso. Quedan ${remaining.length + 1}.` : `Hoja guardada. Quedan ${remaining.length + 1}.`)
      } catch {
        setBatchPhotos(remaining)
        setWorking(undefined)
        setBatchTotal(0)
        setAutoAnalyze(false)
        setScreen('capture')
        showToast('Hoja guardada, pero no se pudo preparar la siguiente fotografía.')
      }
      return
    }
    setWorking(undefined)
    setBatchTotal(0)
    setAutoAnalyze(false)
    setScreen(wasEditing ? afterEdit : 'sheets')
    showToast(result.warnings.length ? `Hoja guardada. Aviso: ${result.warnings.join(' ')}` : 'Hoja guardada correctamente.')
  }

  const openFamily = (ring: string) => {
    setFamilyRing(normalizeRing(ring))
    setFamilyHistory([])
    setScreen('family')
  }
  const navigateFamily = (ring: string) => {
    setFamilyHistory(history => [...history, familyRing])
    setFamilyRing(normalizeRing(ring))
  }
  const backFamily = () => {
    if (familyHistory.length) {
      const history = [...familyHistory]
      setFamilyRing(history.pop() || '')
      setFamilyHistory(history)
    } else setScreen('search')
  }
  const open = (id: string, back: Screen = 'sheets') => {
    setSelectedId(id)
    setDetailBack(back)
    setScreen('detail')
  }

  return (
    <div className="app-shell">
      {screen === 'home' && <Home navigate={setScreen} count={sheets.length} pendingCount={pendingCount} />}
      {screen === 'capture' && <CapturePhoto back={() => setScreen('home')} next={(photos, firstPrepared, anio) => { setBatchPhotos(photos.slice(1)); setBatchTotal(photos.length); setWorking({ ...emptyDraft(), anio, photo: firstPrepared }); setAutoOcr(false); setAutoAnalyze(true); setScreen('form') }} manual={anio => { setBatchPhotos([]); setBatchTotal(0); setWorking({ ...emptyDraft(), anio }); setAutoOcr(false); setAutoAnalyze(false); setScreen('form') }} />}
      {screen === 'form' && working && <SheetForm key={working.id || `${working.photo?.name}-${working.photo?.lastModified}` || 'new'} initial={working} autoOcr={autoOcr} autoAnalyze={autoAnalyze} batchLabel={!working.id && batchTotal > 1 ? `${batchTotal - batchPhotos.length}/${batchTotal}` : undefined} back={() => { if (working.id) setScreen('detail'); else { setBatchPhotos([]); setBatchTotal(0); setAutoAnalyze(false); setScreen('capture') } }} saved={onSaved} />}
      {screen === 'sheets' && <Sheets back={() => setScreen('home')} sheets={sheets} open={id => open(id, 'sheets')} />}
      {screen === 'search' && <BirdSearch back={() => setScreen('home')} openSheet={id => open(id, 'search')} openFamily={openFamily} />}
      {screen === 'family' && familyRing && <FamilyView ring={familyRing} back={backFamily} navigate={navigateFamily} openSheet={id => open(id, 'family')} />}
      {screen === 'detail' && selectedId && <SheetDetailView id={selectedId} back={() => setScreen(detailBack)} edit={sheet => { setBatchPhotos([]); setBatchTotal(0); setAfterEdit(detailBack); setWorking(sheet); setAutoOcr(false); setAutoAnalyze(false); setScreen('form') }} />}
      {screen === 'export' && <ExportExcel back={() => setScreen('home')} sheets={sheets} openSheet={id => open(id, 'export')} />}
      {screen === 'pending' && <PendingStatusReview back={() => setScreen('home')} changed={() => void refreshPendingCount()} />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  )
}

